import { useCallback } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import { mlListMachines, mlListProcesses } from '@/features/machine-learning/api/MLApiClient';
import type { MLMachineSummary, MLProcessSummary } from '@/features/machine-learning/type/MLTypes';
import type { BaseResponse } from '@/lib/http/HttpClient';

type MLMachinesResponse = BaseResponse & { items: MLMachineSummary[] };
type MLProcessesResponse = BaseResponse & { items: MLProcessSummary[] };

export function useMLMachines() {
  const http = useApiClient();
  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      const items = await mlListMachines(http ?? undefined);
      return {
        ok: true as const,
        data: { timestamp: new Date().toISOString(), items },
      };
    },
    [http]
  );

  const { data, isLoading, error, retry } = useApiCall<MLMachinesResponse>({
    apiFn,
    immediate: true,
    deps: [],
  });

  return {
    data: data?.items ?? null,
    isLoading,
    error,
    retry,
  };
}

export function useMLProcesses(machineId: number | null) {
  const http = useApiClient();
  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      const items = await mlListProcesses(machineId!, http ?? undefined);
      return {
        ok: true as const,
        data: { timestamp: new Date().toISOString(), items },
      };
    },
    [machineId, http]
  );

  const { data, isLoading, error, retry } = useApiCall<MLProcessesResponse>({
    apiFn,
    immediate: machineId !== null,
    deps: [machineId],
  });

  return {
    data: data?.items ?? null,
    isLoading,
    error,
    retry,
  };
}
